import { useState, useCallback, useEffect } from 'react'
import useGlobalCustomerStore from '@/stores/globalCustomerStore'
import { partnerService } from '@/services/partnerService'
import { assetService } from '@/services/assetService'
import { activationService } from '@/services/activationService'
import { attributionTagService } from '@/services/attributionTagService'

const STORAGE_KEY_PREFIX = 'commercialization-roadmap-dismissed-'

export interface CommercializationStatus {
  hasPartners: boolean
  hasAttributionTags: boolean
  hasAssets: boolean
  hasActivations: boolean
  isLoading: boolean
  isFirstTimeUser: boolean
  completedSteps: number
  totalSteps: number
  dismissed: boolean
  dismissRoadmap: () => void
  refresh: () => void
}

interface EntityCounts {
  partners: number
  attributionTags: number
  assets: number
  activations: number
}

export function useCommercializationStatus(): CommercializationStatus {
  const { selectedCustomer } = useGlobalCustomerStore()
  const customerId = selectedCustomer?.id || null

  const [isLoading, setIsLoading] = useState(true)
  const [counts, setCounts] = useState<EntityCounts>({
    partners: 0,
    attributionTags: 0,
    assets: 0,
    activations: 0
  })
  const [dismissed, setDismissed] = useState<boolean>(false)

  // Fetch counts from API
  const fetchCounts = useCallback(async (custId: string) => {
    setIsLoading(true)

    try {
      // Fetch all counts in parallel with limit=1 to minimize data transfer
      const [partnersResult, assetsResult, activationsResult, tagsResult] = await Promise.all([
        partnerService.getPartners(custId, undefined, undefined, undefined, undefined, 1, 1)
          .catch(() => ({ pagination: { totalPartners: 0 } })),
        assetService.getAssets(custId, undefined, undefined, undefined, undefined, 1, 1)
          .catch(() => ({ pagination: { totalAssets: 0 } })),
        activationService.getActivations(custId, undefined, undefined, undefined, undefined, 1, 1)
          .catch(() => ({ pagination: { totalActivations: 0 } })),
        attributionTagService.getHashtagsList(custId, 'attribution')
          .catch(() => ({ total: 0 }))
      ])

      setCounts({
        partners: partnersResult.pagination?.totalPartners ?? 0,
        assets: assetsResult.pagination?.totalAssets ?? 0,
        activations: activationsResult.pagination?.totalActivations ?? 0,
        attributionTags: tagsResult.total ?? 0
      })
    } catch (error) {
      console.error('Error fetching commercialization counts:', error)
      // On error, assume no data (will show modal)
      setCounts({
        partners: 0,
        attributionTags: 0,
        assets: 0,
        activations: 0
      })
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Load dismissed state and fetch counts when customer changes
  useEffect(() => {
    if (customerId) {
      // Load dismissed state for this customer
      try {
        const key = `${STORAGE_KEY_PREFIX}${customerId}`
        setDismissed(localStorage.getItem(key) === 'true')
      } catch {
        setDismissed(false)
      }

      // Fetch counts from API
      fetchCounts(customerId)
    } else {
      setIsLoading(false)
      setCounts({
        partners: 0,
        attributionTags: 0,
        assets: 0,
        activations: 0
      })
      setDismissed(false)
    }
  }, [customerId, fetchCounts])

  // Determine existence of each entity type
  const hasPartners = counts.partners > 0
  const hasAttributionTags = counts.attributionTags > 0
  const hasAssets = counts.assets > 0
  const hasActivations = counts.activations > 0

  // First time user has none of the entities
  const isFirstTimeUser = !hasPartners && !hasAttributionTags && !hasAssets && !hasActivations

  // Count completed steps
  const completedSteps =
    (hasPartners ? 1 : 0) +
    (hasAttributionTags ? 1 : 0) +
    (hasAssets ? 1 : 0) +
    (hasActivations ? 1 : 0)

  const totalSteps = 4

  const dismissRoadmap = useCallback(() => {
    if (customerId) {
      try {
        const key = `${STORAGE_KEY_PREFIX}${customerId}`
        localStorage.setItem(key, 'true')
      } catch {
        // localStorage not available
      }
    }
    setDismissed(true)
  }, [customerId])

  // Allow manual refresh of counts
  const refresh = useCallback(() => {
    if (customerId) {
      fetchCounts(customerId)
    }
  }, [customerId, fetchCounts])

  return {
    hasPartners,
    hasAttributionTags,
    hasAssets,
    hasActivations,
    isLoading,
    isFirstTimeUser,
    completedSteps,
    totalSteps,
    dismissed,
    dismissRoadmap,
    refresh
  }
}

export default useCommercializationStatus
